import { ArrowLeft, Info, Layers } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  MATERIALS_BOUNDARIES,
  MATERIALS_CHEMISTRY_START_YEAR,
  MATERIALS_CHEMISTRY_TOOL_IDENTITY,
  MATERIALS_CONDITIONS,
  MATERIALS_MODE_YEARS,
  MATERIALS_MODES,
  MATERIALS_SOURCES,
  type MaterialsMode,
} from './materialsChemistry';
import { MaterialsChemistryCaseQuiz } from './MaterialsChemistryCaseQuiz';
import { MaterialsNanoExplorer } from './MaterialsNanoExplorer';
import { StudyShell } from './StudyShell';
import { toolsHubPath } from './studyTools';
import { modeForYear, useChemistryToolYear } from './useChemistryToolYear';

const MODE_COPY: Record<MaterialsMode, { tab: string; eyebrow: string; title: string; description: string }> = {
  metals: {
    tab: 'Metallic bonding',
    eyebrow: 'Cations in a sea of electrons',
    title: 'Explain metal properties with one model',
    description: 'Use the electron-sea model to explain conductivity, malleability, lustre and melting-point trends.',
  },
  alloys: {
    tab: 'Alloys',
    eyebrow: 'Substitutional, interstitial and heat treatment',
    title: 'Change a metal’s properties',
    description: 'Decide how added atoms fit into a metal lattice, then predict what quenching, tempering and annealing do.',
  },
  extraction: {
    tab: 'Extraction',
    eyebrow: 'Reactivity decides the method',
    title: 'Get the metal out of its ore',
    description: 'Compare each metal with carbon in the reactivity series to choose electrolysis, carbon reduction or heating.',
  },
  nano: {
    tab: 'Nanomaterials',
    eyebrow: '1 to 100 nanometres',
    title: 'Why size changes properties',
    description: 'Classify particle sizes, calculate surface-area-to-volume ratios and explain nanoscale behaviour.',
  },
};

export function MaterialsChemistryLabPage() {
  const year = useChemistryToolYear();
  const [mode, setMode] = useState<MaterialsMode>(() => modeForYear(MATERIALS_MODE_YEARS, year));
  const copy = MODE_COPY[mode];

  return (
    <StudyShell scienceTutor={{ subjectKey: 'chemistry', toolId: 'materials-chemistry', state: { mode } }}>
      <Link
        to={toolsHubPath('chemistry')}
        className="inline-flex items-center gap-2 text-sm font-extrabold text-slate2 hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Chemistry tools
      </Link>

      <header
        className="chemistry-tool-header mt-5 flex flex-col gap-5 rounded-[2rem] border border-brand-sky/25 bg-gradient-to-br from-wash-sky via-canvas-pure to-wash-coral p-6 shadow-card-soft lg:flex-row lg:items-end lg:justify-between lg:p-8"
        data-tool-key={MATERIALS_CHEMISTRY_TOOL_IDENTITY.toolKey}
        data-tool-version={MATERIALS_CHEMISTRY_TOOL_IDENTITY.toolVersion}
      >
        <div className="max-w-3xl">
          <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.16em] text-brand-sky">
            <Layers className="h-4 w-4" aria-hidden="true" /> Water &amp; materials
          </p>
          <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">Materials Chemistry Lab</h1>
          <p className="mt-3 max-w-2xl font-medium leading-7 text-slate2">
            Explain metals with metallic bonding, design alloys, choose how to extract a metal from its ore and see
            why nanoparticles behave differently.
          </p>
        </div>
        <div className="rounded-2xl border border-white/80 bg-white/75 px-4 py-3 text-sm font-bold text-ink-soft">
          Year {MATERIALS_CHEMISTRY_START_YEAR}–12 · explore only
        </div>
      </header>

      {year !== null && year < MATERIALS_CHEMISTRY_START_YEAR ? (
        <p role="note" className="mt-4 rounded-2xl border border-brand-sunshine/50 bg-wash-sunshine px-5 py-3 text-sm font-medium text-ink-soft">
          This tool starts at Year {MATERIALS_CHEMISTRY_START_YEAR}. You are viewing Year {year}; you can still explore it.
        </p>
      ) : null}

      <div
        className="mt-6 flex max-w-full flex-wrap gap-1 rounded-[1.5rem] border border-hairline bg-canvas-pure p-1 shadow-card-soft sm:inline-flex sm:rounded-full"
        role="tablist"
        aria-label="Materials chemistry mode"
      >
        {MATERIALS_MODES.map((candidate) => (
          <button
            key={candidate}
            type="button"
            role="tab"
            aria-selected={mode === candidate}
            onClick={() => setMode(candidate)}
            className={`rounded-full px-4 py-2.5 text-sm font-extrabold ${mode === candidate ? 'bg-ink text-white' : 'text-slate2'}`}
          >
            {MODE_COPY[candidate].tab}
          </button>
        ))}
      </div>

      <div className="mt-6">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">{copy.eyebrow}</p>
        <h2 className="mt-1 text-2xl font-extrabold text-ink">{copy.title}</h2>
        <p className="mt-1 font-medium text-slate2">{copy.description}</p>
      </div>

      <div key={mode} className="mt-5 space-y-6">
        {mode === 'nano' ? <MaterialsNanoExplorer /> : null}
        <MaterialsChemistryCaseQuiz mode={mode} />
      </div>

      <section className="my-6 rounded-[1.5rem] border border-hairline bg-surface p-5 text-sm font-medium leading-6 text-slate2">
        <p className="flex items-center gap-2 font-extrabold text-ink">
          <Info className="h-4 w-4" aria-hidden="true" /> Model boundaries and sources
        </p>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li>{MATERIALS_CONDITIONS}</li>
          {MATERIALS_BOUNDARIES[mode].map((line) => (
            <li key={line}>{line}</li>
          ))}
          <li>
            Progress is not saved, mastery is not recorded and no state syllabus alignment is claimed. Values are
            pending teaching review.
          </li>
        </ul>
        <p className="mt-3">
          Sources: {MATERIALS_SOURCES.map((source) => `${source.label} — ${source.detail}`).join(' ')}
        </p>
      </section>
    </StudyShell>
  );
}
